import { Component, h, Prop, Watch, State } from '@stencil/core';
import { fetchNavigation } from '../menu-service';
import {
    Category,
    CategoryItem,
    NavigationStructure,
} from '../navigation-structure.models';
import { images } from '../../helpers/inline-images.service';

@Component({
    tag: 'pnl-footer',
    styleUrl: 'pnl-footer.scss',
    shadow: true,
})
export class PnlFooter {
    @State() navigation: NavigationStructure;
    @State() expandedElementIndex = -1;

    /** The domain of the client application */
    @Prop() domain: 'postnl.nl' | 'postnl.be' = 'postnl.nl';
    /** The language to show the footer in (en_GB, nl_NL, nl_BE and fr_BE) */
    @Prop() locale = 'nl_NL';
    /** If true, only the bottom most part of the footer will be rendered */
    @Prop() legalOnly: boolean;

    @Watch('locale')
    @Watch('domain')
    async loadFooter() {
        this.navigation = await fetchNavigation(this.domain, this.locale);
    }

    async componentWillLoad() {
        this.loadFooter();
    }

    toggleExpansion(itemIndex) {
        this.expandedElementIndex =
            this.expandedElementIndex === itemIndex ? null : itemIndex;
    }

    private getIdForListItem(itemIndex: number) {
        return `footerRenderedListItem${itemIndex}`;
    }

    private renderMain(items: Category[]) {
        return (
            <div class="pnl-footer--general">
                <div class="pnl-footer__container">
                <h2 class="visually-hidden">{this.navigation.generalLabels?.footerTitle}</h2>                
                    <ul class="pnl-footer__list pnl-footer__list--general">
                        {items.map((item, index) =>
                            this.renderListItem(item, index)
                        )}
                    </ul>
                </div>
            </div>
        );
    }

    private renderLegal(items: CategoryItem[]) {
        return (
            <div class="pnl-footer--legal">
                <div class="pnl-footer__container">
                    <ul class="pnl-footer__list pnl-footer__list--legal">
                        {items.map((item) => this.renderListItemLine(item))}
                    </ul>
                </div>
            </div>
        );
    }

    private renderListItem(item: Category, itemIndex: number) {
        return (
            <li class="pnl-footer__listitem">
                <h3 class="pnl-footer__list-title">{item.title}</h3>
                <button
                    class="pnl-footer__toggle-button"
                    aria-expanded={
                        this.expandedElementIndex === itemIndex
                            ? 'true'
                            : 'false'
                    }
                    onClick={() => this.toggleExpansion(itemIndex)}
                >
                    {item.title}
                    <span
                        class="pnl-footer__toggle-icon"
                        innerHTML={images.arrow_up}
                    >
                        <span class="visually-hidden">Open submenu</span>
                    </span>
                </button>
                <ul
                    id={this.getIdForListItem(itemIndex)}
                    class="pnl-footer__list pnl-footer__list--nested"
                >
                    {item.items.map((item) => this.renderListItemLine(item))}
                </ul>
            </li>
        );
    }

    private renderListItemLine(item: CategoryItem) {
        return (
            <li class="pnl-footer__listitem">
                <a class="pnl-footer__link" href={item.link}>
                    {item.image !== undefined && item.image !== null && (
                        <img
                            class="pnl-footer__image"
                            src={item.image}
                            alt={item.imageAlt}
                        ></img>
                    )}
                    <span>{item.title}</span>
                </a>
            </li>
        );
    }

    render() {
        if (!this.navigation) {
            return;
        }

        return (
            <footer class="pnl-footer">
                {!this.legalOnly &&
                    this.renderMain(this.navigation.footer.global.categories)}
                {this.renderLegal(this.navigation.footer.legal.items)}
            </footer>
        );
    }
}
